import React, { useState } from "react";
import { Form, Button, Carousel } from "react-bootstrap";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import logo from "assets/logo.png";
import image1 from "../../assets/image1.png";
import image2 from "../../assets/image2.png";
import image3 from "../../assets/image3.png";
import CustomNavbarClients from "./components/navBarClients/CustomNavbarClients";
import { loginWithGoogle, registerClient } from "../../api/auth";
import "./RegisterClients.scss";
import { GoogleJwtPayload } from "../../interfaces/IGoogle";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../general/ConfirmationModal";
import {
  faArrowLeft,
  faCheckCircle,
  faExclamationCircle,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import VerificationCodeModalNewAccount from "../general/VerificationCodeModalNewAccount";
import LoadingScreen from "../general/LoadingScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ERROR_MESSAGES } from "../../constants/userConstants";
import { validateEmail, validatePassword } from "../../utils/validators";

const RegisterClients: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [nameError, setNameError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailHasReadyExists, setEmailHasReadyExists] = useState(false);

  const navigate = useNavigate();

  const handleRegister = async () => {
    let isValid = true;

    if (!name.trim()) {
      setNameError("El nombre es requerido");
      isValid = false;
    } else {
      setNameError("");
    }

    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      isValid = false;
    } else {
      setEmailError("");
    }

    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      isValid = false;
    } else {
      setPasswordError("");
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Confirmar la contraseña es requerido");
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError("Las contraseñas no coinciden");
      isValid = false;
    } else {
      setConfirmPasswordError("");
    }

    if (isValid) {
      setIsLoading(true);

      try {
        const response = await registerClient(name, email, password);
        if (response.success) {
          localStorage.setItem("registrationCacheKey", response.cacheKey);
          setIsLoading(false);
          setShowVerificationModal(true);
        } else {
          setIsLoading(false);
          setShowServerErrorModal(true);
        }
      } catch (error: any) {
        setIsLoading(false);
        setShowServerErrorModal(true);
        if (
          error?.response?.data.errors.email[0] ===
          "The email has already been taken."
        ) {
          setEmailHasReadyExists(true);
        }
        console.error(
          "Error creating user:",
          error?.response?.data.errors.email[0] || error
        );
      }
    }
  };

  const handleGoogleLoginSuccess = async (
    credentialResponse: CredentialResponse
  ) => {
    const credentialResponseDecoded: GoogleJwtPayload = jwtDecode(
      credentialResponse.credential ?? ""
    );
    const email = credentialResponseDecoded.email;
    const name = credentialResponseDecoded.name;

    try {
      const response = await loginWithGoogle(
        email,
        name,
        credentialResponse.credential ?? ""
      );

      if (response.success) {
        setShowConfirmationModal(true);
        setTimeout(() => {
          setShowConfirmationModal(false);
          navigate("/");
        }, 5000);
      } else {
        console.error("Error al iniciar sesión con Google:", response.message);
      }
    } catch (error) {
      console.error("Error al iniciar sesión con Google:", error);
    }
  };

  return (
    <>
      <CustomNavbarClients />
      <Button
        variant="secondary"
        onClick={() => navigate("/")}
        className="back-button"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        <span className="back-text" style={{ marginLeft: "5px" }}>
          Regresar
        </span>
      </Button>
      {isLoading && (
        <LoadingScreen loadingText="Enviando correo de autenticación..." />
      )}
      <div className="register-container">
        <div className="register-form">
          <img src={logo} alt="Logo de la Boutique" className="logo" />
          <h1 className="mb-4 text-center fw-bold">Crear cuenta</h1>
          <p className="text-center mb-4" style={{ width: "70%" }}>
            Por favor, ingresa tu correo electrónico y crea una contraseña.
            Asegúrate de que las contraseñas coincidan.
          </p>
          <Form>
            <Form.Group className="text-center">
              <div
                className="mt-1 text-center fw-bold"
                style={{ marginRight: "10rem" }}
              >
                <Form.Label>Nombre</Form.Label>
              </div>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`mb-2 mx-auto ${nameError ? "is-invalid" : ""}`}
                style={{ width: "55%" }}
              />
              {nameError && (
                <Form.Text className="text-danger d-block">
                  {nameError}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group>
              <div
                className="mt-1 text-center fw-bold"
                style={{ marginRight: "5rem" }}
              >
                <Form.Label>Correo electrónico</Form.Label>
              </div>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`mb-2 mx-auto ${emailError ? "is-invalid" : ""}`}
                style={{ width: "55%" }}
              />
              {emailError && (
                <div className="text-center">
                  <Form.Text className="text-danger d-block">
                    {emailError}
                  </Form.Text>
                </div>
              )}
            </Form.Group>

            <Form.Group>
              <div
                className="mt-1 text-center fw-bold"
                style={{ marginRight: "8rem" }}
              >
                <Form.Label>Contraseña</Form.Label>
              </div>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`mb-2 mx-auto ${passwordError ? "is-invalid" : ""}`}
                style={{ width: "55%" }}
              />
              {passwordError && (
                <div className="text-center">
                  <Form.Text className="text-danger">{passwordError}</Form.Text>
                </div>
              )}
            </Form.Group>

            <Form.Group>
              <div
                className="mt-1 text-center fw-bold"
                style={{ marginRight: "3rem" }}
              >
                <Form.Label>Confirmar contraseña</Form.Label>
              </div>
              <div className="d-flex justify-content-center">
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`mb-2 mx-auto ${
                    confirmPasswordError ? "is-invalid" : ""
                  }`}
                  style={{ width: "55%" }}
                />
              </div>
              {confirmPasswordError && (
                <div className="text-center">
                  <Form.Text className="text-danger">
                    {confirmPasswordError}
                  </Form.Text>
                </div>
              )}
            </Form.Group>

            <div className="mt-1 fw-bold text-center">
              <Button
                onClick={handleRegister}
                style={{ width: "35%" }}
                className="mt-4 fw-bold align-item-center text-center"
              >
                Registrarse
              </Button>
            </div>
          </Form>
          <div className="divider-container">
            <hr />
            <div className="divider-circle"></div>
            <hr />
          </div>
          <GoogleLogin onSuccess={handleGoogleLoginSuccess} />
        </div>
        <div className="image-carousel">
          <Carousel>
            <Carousel.Item>
              <img src={image1} alt="Slide 1" />
            </Carousel.Item>
            <Carousel.Item>
              <img src={image2} alt="Slide 2" />
            </Carousel.Item>
            <Carousel.Item>
              <img src={image3} alt="Slide 3" />
            </Carousel.Item>
          </Carousel>
        </div>
      </div>
      <VerificationCodeModalNewAccount
        show={showVerificationModal}
        onHide={() => setShowVerificationModal(false)}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        email={email}
      />
      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        title="Verificación Exitosa"
        message="Tu cuenta ha sido verificada correctamente. Ahora puedes iniciar sesión."
        icon={faCheckCircle}
        iconColor="text-success"
      />

      <ConfirmationModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error de Verificación"
        message="El código de verificación es incorrecto. Por favor, intenta nuevamente."
        icon={faXmarkCircle}
        iconColor="text-danger"
      />
      <ConfirmationModal
        show={showServerErrorModal}
        onHide={() => setShowServerErrorModal(false)}
        title="Error del Servidor"
        message={
          emailHasReadyExists
            ? ERROR_MESSAGES.emailHasAlreadyUsed
            : ERROR_MESSAGES.createUserError
        }
        icon={faExclamationCircle}
        iconColor="text-warning"
      />
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => {
          setShowConfirmationModal(false);
          navigate("/");
        }}
        title="Confirmación"
        message="Cuenta creada exitonsamente, inicia sesión ahora con tu cuenta de google"
        icon={faCheckCircle}
        iconColor="text-success"
      />
    </>
  );
};

export default RegisterClients;
